<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { dispatch, getState, hasSession, join, logout as logoutApi } from './api';
import { avatarOptions } from './seed';
import type { AppAction, AppState, Restaurant, TabId } from './types';

interface InstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const tabs: Array<{ id: TabId; icon: string; label: string }> = [
  { id: 'nearby', icon: '⌖', label: '发现' },
  { id: 'favorites', icon: '♡', label: '收藏池' },
  { id: 'draw', icon: '✦', label: '抽一个' },
  { id: 'meals', icon: '▤', label: '聚餐' },
  { id: 'me', icon: '◉', label: '我的' },
];

const activeTab = ref<TabId>('nearby');
const state = ref<AppState | null>(null);
const loading = ref(true);
const joining = ref(false);
const mutating = ref(false);
const joinCode = ref('');
const joinName = ref('');
const selectedAvatarIndex = ref(0);
const errorMessage = ref('');
const toast = ref('');
const search = ref('');
const category = ref('全部');
const selectedRestaurant = ref<Restaurant | null>(null);
const showRating = ref(false);
const ratingScore = ref(5);
const ratingComment = ref('');
const showMealForm = ref(false);
const mealRestaurantId = ref('');
const mealParticipantIds = ref<string[]>([]);
const mealDate = ref(new Date().toISOString().slice(0, 16));
const mealDishes = ref('');
const mealNote = ref('');
const drawCategory = ref('全部');
const drawing = ref(false);
const drawResult = ref<Restaurant | null>(null);
const installPrompt = ref<InstallPromptEvent | null>(null);
const showInstallHelp = ref(false);
const isOnline = ref(navigator.onLine);

const categories = computed(() => [
  '全部',
  ...new Set(state.value?.restaurants.map((item) => item.category) ?? []),
]);

const visibleRestaurants = computed(() => {
  const keyword = search.value.trim().toLowerCase();
  return (state.value?.restaurants ?? []).filter((item) => {
    const categoryMatch = category.value === '全部' || item.category === category.value;
    const keywordMatch = !keyword || `${item.name}${item.address}${item.tags.join('')}`.toLowerCase().includes(keyword);
    return categoryMatch && keywordMatch;
  });
});

const favoriteRestaurants = computed(() =>
  (state.value?.favorites ?? [])
    .map((favorite) => ({ favorite, restaurant: findRestaurant(favorite.restaurantId) }))
    .filter((item): item is { favorite: AppState['favorites'][number]; restaurant: Restaurant } => Boolean(item.restaurant)),
);

const myMeals = computed(() => {
  if (!state.value) return [];
  const userId = state.value.currentUser.id;
  return [...state.value.meals]
    .filter((meal) => meal.creatorUserId === userId || meal.participantUserIds.includes(userId))
    .sort((a, b) => b.occurredAt.localeCompare(a.occurredAt));
});

const myRatings = computed(() => {
  if (!state.value) return [];
  return state.value.ratings.filter((item) => item.userId === state.value?.currentUser.id);
});

const myDrawCount = computed(() => state.value
  ? state.value.draws.filter((item) => item.userId === state.value?.currentUser.id).length
  : 0);

const drawCandidates = computed(() => favoriteRestaurants.value
  .map((item) => item.restaurant)
  .filter((item) => drawCategory.value === '全部' || item.category === drawCategory.value));

function findRestaurant(id: string): Restaurant | undefined {
  return state.value?.restaurants.find((item) => item.id === id);
}

function findUser(id: string) {
  return state.value?.users.find((item) => item.id === id);
}

function favoriteFor(id: string) {
  return state.value?.favorites.find((item) => item.restaurantId === id);
}

function ratingsFor(id: string) {
  return state.value?.ratings.filter((item) => item.restaurantId === id) ?? [];
}

function averageRating(id: string): string {
  const items = ratingsFor(id);
  if (!items.length) return '暂无';
  return (items.reduce((sum, item) => sum + item.score, 0) / items.length).toFixed(1);
}

function setTab(tab: TabId) {
  activeTab.value = tab;
  window.location.hash = tab;
  window.scrollTo({ top: 0, behavior: 'smooth' });
}

function notify(message: string) {
  toast.value = message;
  window.setTimeout(() => { if (toast.value === message) toast.value = ''; }, 2400);
}

async function perform(action: AppAction, success?: string) {
  if (!state.value || mutating.value) return;
  mutating.value = true;
  try {
    state.value = await dispatch(state.value, action);
    if (success) notify(success);
  } catch (error) {
    notify(error instanceof Error ? error.message : '操作失败，请重试');
  } finally {
    mutating.value = false;
  }
}

async function submitJoin() {
  if (!joinName.value.trim()) {
    errorMessage.value = '先告诉大家怎么称呼你';
    return;
  }
  const option = avatarOptions[selectedAvatarIndex.value];
  if (!option) return;
  joining.value = true;
  errorMessage.value = '';
  try {
    state.value = await join(joinCode.value, joinName.value.trim(), option[0], option[1]);
    notify('欢迎加入饭搭子');
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : '加入失败';
  } finally {
    joining.value = false;
  }
}

async function toggleFavorite(restaurantId: string) {
  const removing = Boolean(favoriteFor(restaurantId));
  await perform({ type: 'FAVORITE_TOGGLE', requestId: crypto.randomUUID(), payload: { restaurantId } }, removing ? '已移出收藏池' : '已加入全员收藏池');
}

async function toggleSupport(restaurantId: string) {
  await perform({ type: 'SUPPORT_TOGGLE', requestId: crypto.randomUUID(), payload: { restaurantId } }, '收藏态度已更新');
}

function startRating(restaurant: Restaurant) {
  selectedRestaurant.value = restaurant;
  const mine = state.value?.ratings.find((item) => item.restaurantId === restaurant.id && item.userId === state.value?.currentUser.id);
  ratingScore.value = mine?.score ?? 5;
  ratingComment.value = mine?.comment ?? '';
  showRating.value = true;
}

async function submitRating() {
  if (!selectedRestaurant.value) return;
  await perform({
    type: 'RATE_RESTAURANT',
    requestId: crypto.randomUUID(),
    payload: { restaurantId: selectedRestaurant.value.id, score: ratingScore.value, comment: ratingComment.value.trim() },
  }, '评分已保存');
  showRating.value = false;
}

function startMeal(restaurantId = '') {
  mealRestaurantId.value = restaurantId || state.value?.restaurants[0]?.id || '';
  mealParticipantIds.value = state.value ? [state.value.currentUser.id] : [];
  mealDate.value = new Date().toISOString().slice(0, 16);
  mealDishes.value = '';
  mealNote.value = '';
  showMealForm.value = true;
}

function toggleParticipant(userId: string) {
  mealParticipantIds.value = mealParticipantIds.value.includes(userId)
    ? mealParticipantIds.value.filter((id) => id !== userId)
    : [...mealParticipantIds.value, userId];
}

async function submitMeal() {
  if (!mealRestaurantId.value || !mealParticipantIds.value.length) {
    notify('请选择餐馆和至少一位参与者');
    return;
  }
  await perform({
    type: 'CREATE_MEAL',
    requestId: crypto.randomUUID(),
    payload: {
      restaurantId: mealRestaurantId.value,
      participantUserIds: mealParticipantIds.value,
      occurredAt: new Date(mealDate.value).toISOString(),
      dishes: mealDishes.value.split(/[，,、\n]/).map((item) => item.trim()).filter(Boolean),
      note: mealNote.value.trim(),
    },
  }, '这顿饭已经记下了');
  showMealForm.value = false;
  setTab('meals');
}

async function drawOne() {
  if (!drawCandidates.value.length || drawing.value) {
    if (!drawCandidates.value.length) notify('先往收藏池放几家餐馆吧');
    return;
  }
  drawing.value = true;
  drawResult.value = null;
  const candidates = drawCandidates.value;
  let ticks = 0;
  const timer = window.setInterval(() => {
    drawResult.value = candidates[Math.floor(Math.random() * candidates.length)] ?? null;
    ticks += 1;
    if (ticks >= 10) window.clearInterval(timer);
  }, 75);
  window.setTimeout(async () => {
    const bytes = new Uint32Array(1);
    crypto.getRandomValues(bytes);
    const selected = candidates[(bytes[0] ?? 0) % candidates.length];
    if (!selected) return;
    drawResult.value = selected;
    drawing.value = false;
    await perform({
      type: 'CREATE_DRAW',
      requestId: crypto.randomUUID(),
      payload: { category: drawCategory.value === '全部' ? undefined : drawCategory.value, candidateIds: candidates.map((item) => item.id), restaurantId: selected.id },
    });
  }, 900);
}

function locate() {
  if (!navigator.geolocation) {
    notify('当前浏览器不支持定位');
    return;
  }
  navigator.geolocation.getCurrentPosition(({ coords }) => {
    if (!state.value) return;
    state.value = {
      ...state.value,
      restaurants: state.value.restaurants.map((restaurant) => ({
        ...restaurant,
        distanceMeters: restaurant.longitude && restaurant.latitude
          ? distance(coords.latitude, coords.longitude, restaurant.latitude, restaurant.longitude)
          : undefined,
      })).sort((a, b) => (a.distanceMeters ?? Infinity) - (b.distanceMeters ?? Infinity)),
    };
    notify('已按当前位置重新排序');
  }, () => notify('没有获得定位权限，可继续搜索和使用其他功能'), { enableHighAccuracy: false, timeout: 8000 });
}

function distance(lat1: number, lon1: number, lat2: number, lon2: number) {
  const rad = Math.PI / 180;
  const a = Math.sin((lat2 - lat1) * rad / 2) ** 2
    + Math.cos(lat1 * rad) * Math.cos(lat2 * rad) * Math.sin((lon2 - lon1) * rad / 2) ** 2;
  return Math.round(12_742_000 * Math.asin(Math.sqrt(a)));
}

function formatDistance(value?: number) {
  if (value === undefined) return '';
  return value < 1000 ? `${value}m` : `${(value / 1000).toFixed(1)}km`;
}

function formatDate(value: string) {
  return new Intl.DateTimeFormat('zh-CN', { timeZone: 'Asia/Shanghai', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(value));
}

async function installApp() {
  if (installPrompt.value) {
    await installPrompt.value.prompt();
    const choice = await installPrompt.value.userChoice;
    if (choice.outcome === 'accepted') notify('正在安装到桌面');
    installPrompt.value = null;
  } else {
    showInstallHelp.value = true;
  }
}

function logOut() {
  if (!window.confirm('确认退出当前饭搭子空间？')) return;
  logoutApi();
  state.value = null;
}

function online() { isOnline.value = true; }
function offline() { isOnline.value = false; }
function captureInstall(event: Event) {
  event.preventDefault();
  installPrompt.value = event as InstallPromptEvent;
}

onMounted(async () => {
  const hash = window.location.hash.slice(1) as TabId;
  if (tabs.some((tab) => tab.id === hash)) activeTab.value = hash;
  window.addEventListener('online', online);
  window.addEventListener('offline', offline);
  window.addEventListener('beforeinstallprompt', captureInstall);
  if (hasSession()) {
    try { state.value = await getState(); } catch { logoutApi(); }
  }
  loading.value = false;
});

onBeforeUnmount(() => {
  window.removeEventListener('online', online);
  window.removeEventListener('offline', offline);
  window.removeEventListener('beforeinstallprompt', captureInstall);
});
</script>

<template>
  <div v-if="loading" class="splash">
    <div class="logo-mark">625</div>
    <p>正在摆好碗筷…</p>
  </div>

  <main v-else-if="!state" class="join-page">
    <section class="join-hero">
      <div class="mini-brand"><span class="brand-dot">625</span><span>吃什么</span></div>
      <div class="hero-visual" aria-hidden="true">
        <span class="hero-bowl">🍚</span><span class="orbit orbit-a">🍜</span><span class="orbit orbit-b">🍲</span><span class="orbit orbit-c">🧋</span>
      </div>
      <p class="eyebrow">只属于熟人的饭桌</p>
      <h1>别纠结了，<br /><em>一起吃点好的。</em></h1>
      <p class="join-copy">共享收藏、认真评分、随机抽签，再把每一顿值得记住的饭保存下来。</p>
    </section>
    <section class="join-card">
      <label>大家怎么称呼你</label>
      <input v-model="joinName" maxlength="12" autocomplete="nickname" placeholder="例如：小林" />
      <label>选择你的饭搭子头像</label>
      <div class="avatar-grid compact">
        <button v-for="(option, index) in avatarOptions" :key="index" :class="{ selected: selectedAvatarIndex === index }" :style="{ background: option[1] }" :aria-label="`头像 ${index + 1}`" @click="selectedAvatarIndex = index">{{ option[0] }}</button>
      </div>
      <label>熟人邀请码</label>
      <input v-model="joinCode" inputmode="numeric" autocomplete="one-time-code" placeholder="输入朋友发来的邀请码" @keyup.enter="submitJoin" />
      <p class="field-note">请输入空间创建者提供的熟人邀请码。</p>
      <p v-if="errorMessage" class="form-error">{{ errorMessage }}</p>
      <button class="primary wide" :disabled="joining" @click="submitJoin">{{ joining ? '正在加入…' : '进入饭桌' }}</button>
      <p class="privacy-note">不需要手机号或邮箱，仅保存你主动填写的昵称和用餐记录。</p>
    </section>
  </main>

  <div v-else class="app-shell">
    <header class="topbar">
      <div class="brand"><span class="brand-dot">625</span><strong>吃什么</strong></div>
      <div class="top-actions"><span class="status-pill" :class="{ offline: !isOnline }"><i></i>{{ isOnline ? '已同步' : '离线' }}</span><button class="avatar-button" :style="{ background: state.currentUser.avatarColor }" @click="setTab('me')">{{ state.currentUser.avatar }}</button></div>
    </header>

    <div class="content">
      <section v-if="activeTab === 'nearby'" class="page">
        <div class="page-intro"><div><p class="eyebrow">今天也要好好吃饭</p><h1>想吃点什么？</h1></div><button class="soft-button" @click="locate">⌖ 定位</button></div>
        <div class="search-box"><span>⌕</span><input v-model="search" placeholder="搜餐馆、地址或标签" /></div>
        <div class="chip-row"><button v-for="item in categories" :key="item" :class="{ active: category === item }" @click="category = item">{{ item }}</button></div>
        <div class="section-heading"><h2>附近好味道</h2><span>{{ visibleRestaurants.length }} 家</span></div>
        <div class="restaurant-list">
          <article v-for="restaurant in visibleRestaurants" :key="restaurant.id" class="restaurant-card" @click="selectedRestaurant = restaurant">
            <div class="food-icon">{{ restaurant.emoji }}</div>
            <div class="restaurant-main">
              <div class="title-line"><h3>{{ restaurant.name }}</h3><span v-if="restaurant.distanceMeters">{{ formatDistance(restaurant.distanceMeters) }}</span></div>
              <p>{{ restaurant.category }} · 人均 ¥{{ restaurant.averagePrice ?? '—' }}</p>
              <div class="rating-line"><strong>内部 {{ averageRating(restaurant.id) }}</strong><span>{{ ratingsFor(restaurant.id).length }} 人评价</span><i></i><span>页面参考 {{ restaurant.referenceRating ?? '—' }}</span></div>
              <div class="tag-row"><span v-for="tag in restaurant.tags" :key="tag">{{ tag }}</span></div>
            </div>
            <button class="heart" :class="{ active: favoriteFor(restaurant.id) }" aria-label="收藏" @click.stop="toggleFavorite(restaurant.id)">{{ favoriteFor(restaurant.id) ? '♥' : '♡' }}</button>
          </article>
        </div>
        <div v-if="!visibleRestaurants.length" class="empty-state"><span>🥢</span><h3>暂时没找到</h3><p>换个关键词或分类试试。</p></div>
      </section>

      <section v-else-if="activeTab === 'favorites'" class="page">
        <div class="page-intro"><div><p class="eyebrow">全员共享</p><h1>我们的收藏池</h1></div><div class="member-stack"><span v-for="user in state.users.slice(0, 3)" :key="user.id" :style="{ background: user.avatarColor }">{{ user.avatar }}</span></div></div>
        <div class="pool-summary"><div><strong>{{ favoriteRestaurants.length }}</strong><span>候选餐馆</span></div><div><strong>{{ new Set(state.favorites.flatMap(item => item.supporterUserIds)).size }}</strong><span>参与表态</span></div><button @click="setTab('draw')">现在抽一个 →</button></div>
        <div class="restaurant-list">
          <article v-for="item in favoriteRestaurants" :key="item.restaurant.id" class="favorite-card">
            <button class="food-icon" @click="selectedRestaurant = item.restaurant">{{ item.restaurant.emoji }}</button>
            <div class="restaurant-main" @click="selectedRestaurant = item.restaurant"><h3>{{ item.restaurant.name }}</h3><p>由 {{ findUser(item.favorite.createdByUserId)?.displayName ?? '成员' }} 收藏</p><div class="rating-line"><strong>内部 {{ averageRating(item.restaurant.id) }}</strong><span>{{ item.restaurant.category }}</span></div></div>
            <button class="support-button" :class="{ active: item.favorite.supporterUserIds.includes(state.currentUser.id) }" @click="toggleSupport(item.restaurant.id)"><span>👍</span>{{ item.favorite.supporterUserIds.length }}</button>
          </article>
        </div>
        <div v-if="!favoriteRestaurants.length" class="empty-state"><span>♡</span><h3>收藏池还是空的</h3><p>去发现页挑几家大家都想吃的。</p><button class="secondary" @click="setTab('nearby')">去逛逛</button></div>
      </section>

      <section v-else-if="activeTab === 'draw'" class="page draw-page">
        <div class="draw-copy"><p class="eyebrow">交给一点好运</p><h1>今天，吃什么？</h1><p>候选只来自大家的共享收藏池。</p></div>
        <div class="draw-machine" :class="{ spinning: drawing }">
          <div class="draw-glow"></div>
          <div v-if="drawResult" class="draw-result"><span>{{ drawResult.emoji }}</span><p>{{ drawing ? '正在认真挑选…' : '就决定是它了' }}</p><h2>{{ drawResult.name }}</h2><small>{{ drawResult.category }} · 人均 ¥{{ drawResult.averagePrice }}</small></div>
          <div v-else class="draw-placeholder"><span>✦</span><h2>{{ drawCandidates.length }} 家候选</h2><p>按下按钮，让纠结停在这里</p></div>
        </div>
        <label class="select-label">想吃的分类</label>
        <select v-model="drawCategory" class="select-control"><option v-for="item in categories" :key="item">{{ item }}</option></select>
        <button class="draw-button" :disabled="drawing || !drawCandidates.length" @click="drawOne"><span>✦</span>{{ drawing ? '抽取中…' : '帮我选一家' }}</button>
        <button v-if="drawResult && !drawing" class="secondary wide" @click="startMeal(drawResult.id)">就吃这家，记一顿</button>
        <p class="fair-note">使用设备安全随机数，抽签结果会保存在你的历史中。</p>
      </section>

      <section v-else-if="activeTab === 'meals'" class="page">
        <div class="page-intro"><div><p class="eyebrow">一起吃过的日子</p><h1>聚餐记录</h1></div><button class="primary small" @click="startMeal()">＋ 记一顿</button></div>
        <div v-if="myMeals.length" class="timeline">
          <article v-for="meal in myMeals" :key="meal.id" class="meal-card"><div class="meal-date"><strong>{{ new Date(meal.occurredAt).getDate() }}</strong><span>{{ new Intl.DateTimeFormat('zh-CN', { month: 'short', timeZone: 'Asia/Shanghai' }).format(new Date(meal.occurredAt)) }}</span></div><div class="meal-body"><div class="title-line"><h3>{{ findRestaurant(meal.restaurantId)?.name }}</h3><span>{{ formatDate(meal.occurredAt) }}</span></div><p v-if="meal.dishes.length">点了：{{ meal.dishes.join('、') }}</p><p v-if="meal.note">{{ meal.note }}</p><div class="meal-people"><span v-for="userId in meal.participantUserIds" :key="userId" :style="{ background: findUser(userId)?.avatarColor }" :title="findUser(userId)?.displayName">{{ findUser(userId)?.avatar }}</span><small>{{ meal.participantUserIds.length }} 人</small></div></div></article>
        </div>
        <div v-else class="empty-state tall"><span>🍽️</span><h3>第一顿等你记录</h3><p>选择餐馆和一起吃饭的人，大家的档案会同步出现。</p><button class="primary" @click="startMeal()">记录第一次聚餐</button></div>
      </section>

      <section v-else class="page profile-page">
        <div class="profile-card"><button class="profile-avatar" :style="{ background: state.currentUser.avatarColor }">{{ state.currentUser.avatar }}</button><div><p class="eyebrow">{{ state.currentUser.role === 'OWNER' ? '饭桌创建者' : '饭搭子成员' }}</p><h1>{{ state.currentUser.displayName }}</h1><span>和 {{ state.users.length - 1 }} 位朋友一起吃饭</span></div></div>
        <div class="stats"><div><strong>{{ myMeals.length }}</strong><span>参加聚餐</span></div><div><strong>{{ myRatings.length }}</strong><span>我的评分</span></div><div><strong>{{ myDrawCount }}</strong><span>抽签次数</span></div></div>
        <div class="profile-section"><h2>我的最近评分</h2><article v-for="rating in myRatings.slice(-3).reverse()" :key="rating.id" class="rating-item"><span>{{ findRestaurant(rating.restaurantId)?.emoji }}</span><div><strong>{{ findRestaurant(rating.restaurantId)?.name }}</strong><p>{{ rating.comment || '没有留下文字' }}</p></div><b>{{ rating.score.toFixed(1) }}</b></article><p v-if="!myRatings.length" class="muted">还没有评分，吃完一顿再回来写。</p></div>
        <div class="settings-list"><button @click="installApp"><span>▣</span><div><strong>安装到手机</strong><small>安卓直接安装；iPhone 添加到主屏幕</small></div><b>›</b></button><button @click="showInstallHelp = true"><span>?</span><div><strong>安装与隐私说明</strong><small>查看数据、定位与离线规则</small></div><b>›</b></button><button class="danger-text" @click="logOut"><span>↪</span><div><strong>退出当前空间</strong><small>本机登录状态将被清除</small></div><b>›</b></button></div>
      </section>
    </div>

    <nav class="bottom-nav"><button v-for="tab in tabs" :key="tab.id" :class="{ active: activeTab === tab.id, draw: tab.id === 'draw' }" @click="setTab(tab.id)"><i>{{ tab.icon }}</i><span>{{ tab.label }}</span></button></nav>

    <div v-if="selectedRestaurant" class="modal-layer" @click.self="selectedRestaurant = null"><section class="sheet restaurant-sheet"><button class="close" @click="selectedRestaurant = null">×</button><div class="detail-hero"><span>{{ selectedRestaurant.emoji }}</span><div><p class="eyebrow">{{ selectedRestaurant.category }}</p><h2>{{ selectedRestaurant.name }}</h2><p>{{ selectedRestaurant.address }}</p></div></div><div class="detail-scores"><div><strong>{{ averageRating(selectedRestaurant.id) }}</strong><span>内部评分 · {{ ratingsFor(selectedRestaurant.id).length }}人</span></div><div><strong>{{ selectedRestaurant.referenceRating ?? '—' }}</strong><span>页面参考 · {{ selectedRestaurant.referenceUpdatedAt?.slice(5, 10) }}</span></div></div><div class="tag-row large"><span v-for="tag in selectedRestaurant.tags" :key="tag">{{ tag }}</span></div><div class="sheet-actions"><button class="secondary" @click="startRating(selectedRestaurant)">☆ 写评分</button><button class="primary" @click="toggleFavorite(selectedRestaurant.id)">{{ favoriteFor(selectedRestaurant.id) ? '移出收藏池' : '加入收藏池' }}</button></div><div class="reviews"><h3>熟人怎么说</h3><article v-for="rating in ratingsFor(selectedRestaurant.id)" :key="rating.id"><span :style="{ background: findUser(rating.userId)?.avatarColor }">{{ findUser(rating.userId)?.avatar }}</span><div><strong>{{ findUser(rating.userId)?.displayName }} · {{ rating.score.toFixed(1) }}</strong><p>{{ rating.comment }}</p></div></article><p v-if="!ratingsFor(selectedRestaurant.id).length" class="muted">还没人评价，等你来当第一个。</p></div></section></div>

    <div v-if="showRating" class="modal-layer" @click.self="showRating = false"><section class="sheet form-sheet"><button class="close" @click="showRating = false">×</button><p class="eyebrow">只在熟人之间可见</p><h2>评价 {{ selectedRestaurant?.name }}</h2><div class="score-picker"><button v-for="score in [1, 2, 3, 4, 5]" :key="score" :class="{ active: score <= ratingScore }" @click="ratingScore = score">★</button><strong>{{ ratingScore.toFixed(1) }}</strong></div><label>说两句（可选）</label><textarea v-model="ratingComment" maxlength="200" placeholder="味道、环境、服务，记下真实感受…"></textarea><button class="primary wide" :disabled="mutating" @click="submitRating">保存评分</button></section></div>

    <div v-if="showMealForm" class="modal-layer" @click.self="showMealForm = false"><section class="sheet form-sheet meal-form"><button class="close" @click="showMealForm = false">×</button><p class="eyebrow">留住这一顿</p><h2>记录聚餐</h2><label>去了哪家</label><select v-model="mealRestaurantId" class="select-control"><option v-for="restaurant in state.restaurants" :key="restaurant.id" :value="restaurant.id">{{ restaurant.name }}</option></select><label>什么时候</label><input v-model="mealDate" type="datetime-local" /><label>和谁一起 · 已选 {{ mealParticipantIds.length }} 人</label><div class="participant-grid"><button v-for="user in state.users" :key="user.id" :class="{ selected: mealParticipantIds.includes(user.id) }" @click="toggleParticipant(user.id)"><span :style="{ background: user.avatarColor }">{{ user.avatar }}</span><small>{{ user.displayName }}</small></button></div><label>吃了什么（用逗号分隔）</label><input v-model="mealDishes" placeholder="例如：毛肚、虾滑、冰粉" /><label>备注</label><textarea v-model="mealNote" maxlength="300" placeholder="这一顿有什么值得记住？"></textarea><button class="primary wide" :disabled="mutating" @click="submitMeal">保存聚餐</button></section></div>

    <div v-if="showInstallHelp" class="modal-layer" @click.self="showInstallHelp = false"><section class="sheet help-sheet"><button class="close" @click="showInstallHelp = false">×</button><p class="eyebrow">像 App 一样使用</p><h2>安装到手机桌面</h2><div class="instruction"><span>Android</span><p>使用 Chrome 打开本站，点右上角菜单，再选择“安装应用”或“添加到主屏幕”。也可以下载我们生成的 APK。</p></div><div class="instruction"><span>iPhone</span><p>必须使用 Safari 打开，点击底部“分享”按钮，然后选择“添加到主屏幕”。</p></div><div class="privacy-box"><strong>隐私说明</strong><p>定位只用于本次距离排序，不保存精确位置；昵称、头像、收藏、评分与聚餐记录只供同一邀请码空间的成员查看。</p></div><button class="primary wide" @click="showInstallHelp = false">知道了</button></section></div>

    <transition name="toast"><div v-if="toast" class="toast">{{ toast }}</div></transition>
  </div>
</template>
